import type {
  RecommendCandidate,
  RecommendProductsOptions,
  ScoredCandidate,
  ScoreParts,
} from './types';
import { compareScoreDescThenId, rank, sameMembership } from './rank';
import { coOccurrenceBoost, scoreSoldProductParts } from './score';

const emptyParts = (total: number): ScoreParts => ({
  popularity: 0,
  keyword: 0,
  city: 0,
  coOccurrence: 0,
  total,
});

/**
 * Score one candidate from sold / fit / co-occur fields.
 * Precomputed `score` short-circuits sold recompute (callers that already ran
 * the builder formula can still get a stable ScoredCandidate shell).
 */
export const scoreCandidate = <T extends RecommendCandidate>(
  candidate: T,
  options: RecommendProductsOptions = {}
): ScoredCandidate<T> => {
  if (typeof candidate.score === 'number' && Number.isFinite(candidate.score)) {
    return {
      ...candidate,
      score: candidate.score,
      scoreParts: emptyParts(candidate.score),
    };
  }

  const co =
    (candidate.coTrips ?? 0) > 0
      ? coOccurrenceBoost(candidate.coTrips ?? 0, {
          lift: candidate.coLift,
          distinctAccounts: candidate.distinctAccounts,
          weight: options.weights?.coOccurrence,
        })
      : 0;

  const parts = scoreSoldProductParts({
    soldTrips: candidate.soldTrips ?? 0,
    cancelledTrips: candidate.cancelledTrips ?? 0,
    keywordHits: candidate.keywordHits ?? 0,
    cityHit: candidate.cityHit === true,
    keywordsActive: options.keywordsActive,
    baselineCancelRate: options.baselineCancelRate,
    weights: options.weights,
    coOccurrence: co,
  });

  return {
    ...candidate,
    score: parts.total,
    scoreParts: parts,
  };
};

/**
 * Shared recommendation entry: score each visible candidate, order best-first.
 *
 * - Input membership is sacred (visibility already applied by the caller).
 * - Output is a permutation (optionally truncated by `limit`).
 * - Empty sold evidence → score 0 ties → stable id order among zeros, which
 *   still preserves relative order among equal scores via id only when scores
 *   differ by float noise; callers that need pure input-order ties should
 *   pre-score with identical values and use `rank` after a stable source order.
 */
export const recommendProducts = <T extends RecommendCandidate>(
  candidates: readonly T[],
  options: RecommendProductsOptions = {}
): ScoredCandidate<T>[] => {
  const scored = candidates.map((row) => scoreCandidate(row, options));
  const ordered = rank(scored);
  if (!sameMembership(candidates, ordered)) {
    // Defensive: rank must never drop/add ids. Throw in dev paths; production
    // callers should never hit this if rank stays a pure sort.
    throw new Error('recommendProducts: rank changed membership');
  }
  const limit = options.limit;
  if (limit == null || limit >= ordered.length) return [...ordered];
  return [...ordered].slice(0, Math.max(0, limit));
};

/** Re-export comparator for domain code that sorts RankedProduct-like rows. */
export { compareScoreDescThenId };
